import type { IAffiliation, IAuthor } from "~/types";
import type { IAffiliationsSchema } from "~/types/models/Affiliations.model";
import type { IAuthorSchema } from "~/types/models/Author.model";
import type { IAuthor2025Schema } from "~/types/models/ResearchTopic2025.model";

export const useAuthorMappers = () => {
  const { loopUrl } = useRuntimeConfig().public;

  function getThumbnailUrl(userId: number): string {
    return `${loopUrl}/images/profile/${userId}/55`;
  }

  function getProfileUrl(userId: number): string {
    return `${loopUrl}/people/${userId}/overview`;
  }

  function mapIAuthorSchemaToIAuthor(author: IAuthorSchema): IAuthor {
    return {
      userId: author.UserId,
      fullName: author.FullName
        ? author.FullName
        : [author.FirstName, author.MiddleName, author.LastName].join(" "),
      thumbnailUrl: getThumbnailUrl(author.UserId),
      profileUrl: getProfileUrl(author.UserId),
      affiliations: author.Affiliations?.map((Affiliation) => {
        return {
          department: Affiliation.Department,
          organization: Affiliation.Organization,
          city: Affiliation.City,
          country: Affiliation.Country,
          address: Affiliation.City
            ? Affiliation.City +
              (Affiliation.Country ? ", " + Affiliation.Country : "")
            : Affiliation.Country
            ? Affiliation.Country
            : ""
        } as IAffiliation;
      })
    };
  }

  function getUniqueAuthors(authors: IAuthorSchema[]): IAuthorSchema[] {
    const uniqueIds = new Set();
    const uniqueEmails = new Set();

    const UniqueAuthors = authors.filter((record) => {
      if (record.UserId > 0) {
        if (!uniqueIds.has(record.UserId)) {
          uniqueIds.add(record.UserId);
          return true;
        }
      } else if (record.Email !== "") {
        if (!uniqueEmails.has(record.Email)) {
          uniqueEmails.add(record.Email);
          return true;
        }
      } else if (record.UserId === 0) {
        return true;
      }
      return false;
    });
    return UniqueAuthors;
  }

  function mapIAuthor2025SchemaToIAuthorSchema(
    author: IAuthor2025Schema
  ): IAuthorSchema {
    return {
      UserId: author.UserId,
      FullName: author.FullName,
      Email: author.EmailId,
      Affiliations: author.Affiliation
        ? [
            {
              AddressLine1: author.Affiliation.AddressLine1,
              AddressLine2: author.Affiliation.AddressLine2,
              AddressLine3: author.Affiliation.AddressLine3,
              City: author.Affiliation.City,
              Country: author.Affiliation.Country,
              Department: author.Affiliation.Department,
              Organization: author.Affiliation.Organization,
              Position: author.Affiliation.Position,
              PostalCode: author.Affiliation.PostalCode,
              State: author.Affiliation.State
            }
          ]
        : ([] as IAffiliationsSchema[])
    };
  }

  return {
    mapIAuthorSchemaToIAuthor,
    getUniqueAuthors,
    mapIAuthor2025SchemaToIAuthorSchema
  };
};
